export interface BackstageContent {
  brandName: string;
  instagramUrl: string;
  heroImageUrl: string;
  heroEyebrow: string;
  heroLineOne: string;
  heroEmphasis: string;
  heroDescription: string;
  releaseLabel: string;
  releaseTitle: string;
  releaseMeta: string;
  releaseArtworkUrl: string;
  releaseStreamUrl: string;
  releaseCtaLabel: string;
  benefitsTitle: string;
  benefitsDescription: string;
  benefits: string[];
  formCta: string;
  smsOptInLabel: string;
  smsConsentText: string;
  successEyebrow: string;
  successTitle: string;
  successDescription: string;
  rewardLabel: string;
  releaseHidden: boolean;
}

export const backstageStorageKey = 'artistos-backstage-content';
export const backstageUpdatedEvent = 'artistos:backstage-updated';

export const defaultBackstageContent: BackstageContent = {
  brandName: 'COOPER TY BAND',
  instagramUrl: 'https://www.instagram.com/coopertyband/',
  heroImageUrl: 'https://cdn.magicpatterns.com/uploads/et89mcquGq3ofLkhxoC2G9/image.png',
  heroEyebrow: 'You found the backstage door',
  heroLineOne: 'Welcome to',
  heroEmphasis: 'the fan club',
  heroDescription: 'The songs, the road stories, and the things I only share with the people who are here from the start.',
  releaseLabel: 'New single',
  releaseTitle: 'Gone Last Week',
  releaseMeta: 'Cooper Ty Band · 2026',
  releaseArtworkUrl: 'https://cdn.magicpatterns.com/uploads/55PpjMwhmz3ZUMQuz9BUEQ/3AE176BF-3876-47EE-AE10-B82013DADEBA.png',
  releaseStreamUrl: 'https://open.spotify.com/album/70hpntB5E2jMk0yUGpyzQ',
  releaseCtaLabel: 'Listen now',
  benefitsTitle: 'Join the inner circle.',
  benefitsDescription: 'No algorithms. No noise. Just a direct line from me to you.',
  benefits: [
    'First access to tickets and presales',
    'Unreleased songs, demos and acoustic sessions',
    'Texas tour dates before everyone else',
  ],
  formCta: 'Unlock backstage',
  smsOptInLabel: 'Text me presales, show alerts, and new music first.',
  smsConsentText: 'By checking this box, you agree to receive recurring automated marketing texts from Cooper Ty Band. Consent is not required to join. Msg & data rates may apply. Reply STOP to opt out.',
  successEyebrow: 'You’re in.',
  successTitle: 'Welcome backstage.',
  successDescription: 'Your unreleased acoustic take is headed to your inbox. Save this page—your first presale notice will show up here.',
  rewardLabel: 'Play acoustic take',
  releaseHidden: false,
};

export function getPublishedBackstageContent(): BackstageContent {
  try {
    const stored = window.localStorage.getItem(backstageStorageKey);
    if (!stored) return defaultBackstageContent;
    return { ...defaultBackstageContent, ...JSON.parse(stored) } as BackstageContent;
  } catch {
    return defaultBackstageContent;
  }
}

export function createBackstagePreviewUrl(content: BackstageContent): string {
  const json = JSON.stringify({ ...defaultBackstageContent, ...content });
  const bytes = new TextEncoder().encode(json);
  const payload = btoa(String.fromCharCode(...bytes));
  // Keep preview data after # so the render server never receives it as a request header.
  return `${window.location.origin}/backstage#preview=${encodeURIComponent(payload)}`;
}

export function getBackstageContentFromUrl(): BackstageContent | null {
  try {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const payload = fragment.get('preview');
    if (!payload) return null;
    const bytes = Uint8Array.from(atob(payload), (character) => character.charCodeAt(0));
    const saved = JSON.parse(new TextDecoder().decode(bytes));
    return { ...defaultBackstageContent, ...saved } as BackstageContent;
  } catch {
    return null;
  }
}

export function publishBackstageContent(content: BackstageContent): boolean {
  try {
    window.localStorage.setItem(backstageStorageKey, JSON.stringify(content));
    window.dispatchEvent(new CustomEvent(backstageUpdatedEvent, { detail: content }));
    return true;
  } catch {
    return false;
  }
}
